import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { supabaseRpcWithRetry, supabaseQueryWithRetry, type RetryConfig } from '@/lib/networkRetry';
import { createLogger } from '@/lib/structuredLogger';
import {
  DashboardProfile,
  DashboardHealthMetrics,
  DashboardData,
  DEFAULT_DASHBOARD_METRICS,
} from '@/types/dashboard';

const logger = createLogger('DashboardData');
const DASHBOARD_FAST_FAIL_RETRY_CONFIG: RetryConfig = {
  maxRetries: 1,
  timeoutMs: 10000,
  baseDelayMs: 350,
  maxDelayMs: 1000,
};

// Local aliases for use within this module; also re-exported for consumers.
type Profile = DashboardProfile;
type HealthMetrics = DashboardHealthMetrics;
export type { Profile, HealthMetrics, DashboardData };

const defaultMetrics = DEFAULT_DASHBOARD_METRICS;

export type RecoveryMetricFallback = {
  metric_date: string | null;
  sleep_hours: number | null;
  sleep_efficiency: number | null;
  sleep_deep_minutes: number | null;
  sleep_rem_minutes: number | null;
  sleep_start_time: string | null;
  hrv_rmssd: number | null;
  hrv_coverage: number | null;
};

const toMetricNumber = (value: unknown): number => (typeof value === 'number' ? value : 0);
const isPositiveNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export function hasRecoveryMetrics(data: Partial<HealthMetrics> | null | undefined): boolean {
  if (!data) return false;
  return isPositiveNumber(data.sleep_hours) || isPositiveNumber(data.hrv_rmssd);
}

export function mergeMissingRecoveryMetrics<TMetrics extends Partial<HealthMetrics>>(
  baseMetrics: TMetrics,
  recoveryMetrics: Partial<HealthMetrics> | null | undefined,
): TMetrics {
  if (!recoveryMetrics) return baseMetrics;

  return {
    ...baseMetrics,
    sleep_hours: isPositiveNumber(baseMetrics.sleep_hours)
      ? baseMetrics.sleep_hours
      : toMetricNumber(recoveryMetrics.sleep_hours),
    sleep_efficiency: isPositiveNumber(baseMetrics.sleep_efficiency)
      ? baseMetrics.sleep_efficiency
      : toMetricNumber(recoveryMetrics.sleep_efficiency),
    sleep_deep_minutes: isPositiveNumber(baseMetrics.sleep_deep_minutes)
      ? baseMetrics.sleep_deep_minutes
      : toMetricNumber(recoveryMetrics.sleep_deep_minutes),
    sleep_rem_minutes: isPositiveNumber(baseMetrics.sleep_rem_minutes)
      ? baseMetrics.sleep_rem_minutes
      : toMetricNumber(recoveryMetrics.sleep_rem_minutes),
    sleep_start_time: baseMetrics.sleep_start_time ?? recoveryMetrics.sleep_start_time ?? null,
    hrv_rmssd: isPositiveNumber(baseMetrics.hrv_rmssd)
      ? baseMetrics.hrv_rmssd
      : toMetricNumber(recoveryMetrics.hrv_rmssd),
    hrv_coverage: isPositiveNumber(baseMetrics.hrv_coverage)
      ? baseMetrics.hrv_coverage
      : toMetricNumber(recoveryMetrics.hrv_coverage),
  };
}

function mapRecoveryFallbackToMetrics(
  row: RecoveryMetricFallback | null | undefined,
): Partial<HealthMetrics> | null {
  if (!row) return null;

  return {
    metric_source_date: row.metric_date ?? undefined,
    sleep_hours: toMetricNumber(row.sleep_hours),
    sleep_efficiency: toMetricNumber(row.sleep_efficiency),
    sleep_deep_minutes: toMetricNumber(row.sleep_deep_minutes),
    sleep_rem_minutes: toMetricNumber(row.sleep_rem_minutes),
    sleep_start_time: row.sleep_start_time ?? null,
    hrv_rmssd: toMetricNumber(row.hrv_rmssd),
    hrv_coverage: toMetricNumber(row.hrv_coverage),
  };
}

async function fetchRecentRecoveryMetrics(
  userId: string,
  date: string,
  retryConfig?: RetryConfig,
): Promise<Partial<HealthMetrics> | null> {
  const { data, error } = await supabaseQueryWithRetry<RecoveryMetricFallback[]>(async () => {
    const result = await supabase
      .from('health_metrics')
      .select(
        'metric_date, sleep_hours, sleep_efficiency, sleep_deep_minutes, sleep_rem_minutes, sleep_start_time, hrv_rmssd, hrv_coverage',
      )
      .eq('user_id', userId)
      .lte('metric_date', date)
      .order('metric_date', { ascending: false })
      .limit(7);
    return { data: result.data, error: result.error };
  }, retryConfig);

  if (error) {
    logger.warn('Metrics recovery fallback query error', {
      userId,
      date,
      error,
    });
    return null;
  }

  const recoveryRow = data?.find((row) =>
    hasRecoveryMetrics(row as unknown as Partial<DashboardHealthMetrics>),
  );
  return mapRecoveryFallbackToMetrics(recoveryRow);
}

/**
 * Fetch user profile
 *
 * @param userId - User ID to fetch profile for
 * @param session - Supabase session (for auth)
 * @returns Profile or null if not found
 */
export async function fetchProfile(
  userId: string,
  _session: Session,
  retryConfig?: RetryConfig,
): Promise<Profile | null> {
  try {
    const { data, error } = await supabaseQueryWithRetry<Profile>(async () => {
      const result = await supabase
        .from('profiles')
        .select('id, email, avatar_url, consent_data_processing')
        .eq('id', userId)
        .maybeSingle();
      return { data: (result.data as unknown as Profile | null) ?? null, error: result.error };
    }, retryConfig);

    if (error) {
      logger.error('Profile query error', error, { userId });
      return null;
    }

    return data;
  } catch (err) {
    logger.error('Profile query exception', err instanceof Error ? err : new Error(String(err)), {
      userId,
    });
    throw err;
  }
}

/**
 * Fetch health metrics for a specific date
 *
 * @param userId - User ID to fetch metrics for
 * @param session - Supabase session (for auth)
 * @param date - Date string in YYYY-MM-DD format
 * @returns Health metrics with defaults for missing values
 */
export async function fetchMetrics(
  userId: string,
  _session: Session,
  date: string,
  retryConfig?: RetryConfig,
): Promise<HealthMetrics> {
  try {
    const { data: exactData, error: exactError } = await supabaseQueryWithRetry<
      Partial<DashboardHealthMetrics>
    >(async () => {
      const result = await supabase
        .from('health_metrics')
        .select('*')
        .eq('user_id', userId)
        .eq('metric_date', date)
        .maybeSingle();
      return {
        data: (result.data as unknown as Partial<DashboardHealthMetrics> | null) ?? null,
        error: result.error,
      };
    }, retryConfig);

    if (exactError) {
      logger.error('Metrics exact-date query error', exactError, { userId, date });
    }

    if (exactData) {
      const exactMetrics = {
        ...defaultMetrics,
        ...exactData,
        metric_source_date: date,
      };

      if (hasRecoveryMetrics(exactMetrics)) {
        return exactMetrics;
      }

      const recoveryMetrics = await fetchRecentRecoveryMetrics(userId, date, retryConfig);
      return mergeMissingRecoveryMetrics(exactMetrics, recoveryMetrics ?? null);
    }

    const recoveryMetrics = await fetchRecentRecoveryMetrics(userId, date, retryConfig);
    if (recoveryMetrics) {
      return mergeMissingRecoveryMetrics(
        {
          ...defaultMetrics,
          metric_source_date: recoveryMetrics.metric_source_date,
        },
        recoveryMetrics,
      );
    }

    return defaultMetrics;
  } catch (err) {
    logger.error('Metrics query exception', err instanceof Error ? err : new Error(String(err)), {
      userId,
      date,
    });
    return defaultMetrics;
  }
}

/**
 * Fetch user points total
 *
 * @param userId - User ID to fetch points for
 * @param session - Supabase session (for auth)
 * @returns Total points (0 if error or not found)
 */
export async function fetchPoints(
  userId: string,
  _session: Session,
  retryConfig?: RetryConfig,
): Promise<number> {
  try {
    const { data, error } = await supabaseRpcWithRetry<number>(
      supabase,
      'calculate_points_total',
      { p_user_id: userId },
      retryConfig,
    );
    if (error) throw error;
    if (typeof data !== 'number' || !Number.isSafeInteger(data) || data < 0) {
      throw new Error('Invalid wallet balance');
    }
    return data;
  } catch (err) {
    logger.error('Points query exception', err instanceof Error ? err : new Error(String(err)), {
      userId,
    });
    return 0;
  }
}

/**
 * Calculate user streak using database RPC function
 * Optimized 100x+ faster than client-side loop using SQL window functions
 *
 * @param userId - User ID to calculate streak for
 * @returns Number of consecutive days with health data (0 if none or error)
 */
export async function fetchStreak(userId: string, retryConfig?: RetryConfig): Promise<number> {
  try {
    const { data, error } = await supabaseRpcWithRetry<number>(
      supabase,
      'calculate_user_streak',
      {
        user_id_param: userId,
      },
      retryConfig,
    );

    if (error) {
      logger.error('Streak RPC error', error, { userId });
      return 0;
    }

    return data || 0;
  } catch (error) {
    logger.error(
      'Streak calculation error',
      error instanceof Error ? error : new Error(String(error)),
      { userId },
    );
    return 0;
  }
}

/**
 * Fetch all dashboard data using parallel queries
 *
 * Note: Batched RPC (get_dashboard_data) is not deployed.
 * Using parallel queries directly to avoid 404 errors.
 *
 * @param userId - User ID to fetch data for
 * @param session - Supabase session (for auth)
 * @param date - Date string in YYYY-MM-DD format (defaults to today)
 * @returns Combined dashboard data
 */
export async function fetchDashboardData(
  userId: string,
  session: Session,
  date?: string,
): Promise<DashboardData> {
  const today = date || new Date().toISOString().split('T')[0];
  return fetchDashboardDataFallback(userId, session, today);
}

/**
 * Fetch all dashboard data in parallel
 */
async function fetchDashboardDataFallback(
  userId: string,
  session: Session,
  date: string,
): Promise<DashboardData> {
  const [profile, metrics, points, streak] = await Promise.all([
    fetchProfile(userId, session, DASHBOARD_FAST_FAIL_RETRY_CONFIG),
    fetchMetrics(userId, session, date, DASHBOARD_FAST_FAIL_RETRY_CONFIG),
    fetchPoints(userId, session, DASHBOARD_FAST_FAIL_RETRY_CONFIG),
    fetchStreak(userId, DASHBOARD_FAST_FAIL_RETRY_CONFIG),
  ]);

  return {
    profile: profile ?? {
      id: userId,
      email: session.user.email ?? '',
      avatar_url: null,
      consent_data_processing: false,
    },
    metrics,
    points,
    streak,
  };
}
